import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useSocket } from '../contexts/SocketContext';
import api from '../utils/api';
import {
  Search, Plus, Loader2, Ticket, AlertCircle, Tag, Timer,
  LayoutList, LayoutGrid, Minimize2, Maximize2, Settings2,
  RefreshCw, GripVertical, ChevronRight, User, Clock,
  MessageSquare, Paperclip, ArrowUp, ArrowDown, Minus,
  Flame, Zap, Circle, CheckSquare, Square, X,
  UserPlus, ArrowRightLeft, Megaphone, Filter, Eye, Copy
} from 'lucide-react';
import { formatDate } from '../utils/helpers';
import NewTicketModal from '../components/NewTicketModal';
import useKeyboardShortcuts, { ShortcutsHelp } from '../hooks/useKeyboardShortcuts';

const TicketList = () => {
  const { user, isAgent, isSuperAdmin, accessibleOrganizations } = useAuth();
  const { tenant } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [slaFilter, setSlaFilter] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(() => tenant?.id ? String(tenant.id) : '');
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  
  const [viewMode, setViewMode] = useState(localStorage.getItem('ticketViewMode') || 'list');
  const [compactMode, setCompactMode] = useState(localStorage.getItem('ticketCompactMode') === 'true');
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('ticketVisibleColumns');
    return saved ? JSON.parse(saved) : ['status', 'priority', 'category', 'user', 'date', 'agent'];
  });
  const [refreshing, setRefreshing] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Bulk actions
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [showBulkBar, setShowBulkBar] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastInternal, setBroadcastInternal] = useState(false);

  // Smart Views
  const [smartViews, setSmartViews] = useState([]);
  const [activeSmartView, setActiveSmartView] = useState(null);

  const searchInputRef = useRef(null);

  // Auto-refresh toutes les 30 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTickets(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, priorityFilter, categoryFilter, slaFilter, selectedOrg, search]);

  // Synchroniser selectedOrg quand le tenant change (switch d'organisation)
  useEffect(() => {
    if (tenant?.id) {
      setSelectedOrg(String(tenant.id));
    }
  }, [tenant?.id]);

  // Ouvrir modal si action=new dans URL
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setShowNewTicketModal(true);
      setSearchParams({});
    }
  }, [searchParams]);

  // Écouter l'event pour afficher l'aide raccourcis
  useEffect(() => {
    const handler = () => setShowShortcutsHelp(true);
    window.addEventListener('show-shortcuts-help', handler);
    return () => window.removeEventListener('show-shortcuts-help', handler);
  }, []);

  // Raccourcis clavier
  useKeyboardShortcuts({
    onNewTicket: () => setShowNewTicketModal(true),
    onSearch: () => searchInputRef.current?.focus(),
    onRefresh: () => fetchTickets(),
    onToggleView: () => toggleViewMode(),
    onToggleCompact: () => toggleCompactMode(),
  });

  const fetchTickets = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const params = { 
        search, 
        status: statusFilter, 
        priority: priorityFilter,
        categoryId: categoryFilter,
        slaBreached: slaFilter || undefined
      };
      if (selectedOrg) params.organizationId = selectedOrg;
      const res = await api.get('/tickets', { params });
      setTickets(res.data.tickets);
    } catch (err) {
      console.error('Erreur fetch tickets:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const params = {};
      if (tenant?.id) params.organizationId = tenant.id;
      const res = await api.get('/tickets/categories/list', { params });
      setCategories(res.data);
    } catch (err) {
      console.error('Erreur fetch catégories:', err);
    }
  };

  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;
    const handleRefresh = () => fetchTickets(true);
    socket.on('new_ticket', handleRefresh);
    socket.on('ticket_updated', handleRefresh);
    socket.on('new_message', handleRefresh);
    socket.on('status_changed', handleRefresh);
    return () => {
      socket.off('new_ticket', handleRefresh);
      socket.off('ticket_updated', handleRefresh);
      socket.off('new_message', handleRefresh);
      socket.off('status_changed', handleRefresh);
    };
  }, [socket]);

  useEffect(() => { fetchCategories(); fetchSmartViews(); fetchAvailableAgents(); }, [tenant?.id]);
  useEffect(() => { fetchTickets(); }, [statusFilter, priorityFilter, categoryFilter, slaFilter, selectedOrg]);
  useEffect(() => {
    const timeout = setTimeout(fetchTickets, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Bulk selection management
  useEffect(() => {
    setShowBulkBar(selectedTickets.size > 0);
  }, [selectedTickets]);

  const toggleTicketSelection = (ticketId) => {
    setSelectedTickets(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const selectAllTickets = () => {
    if (selectedTickets.size === tickets.length) {
      setSelectedTickets(new Set());
    } else {
      setSelectedTickets(new Set(tickets.map(t => t.id)));
    }
  };

  const clearSelection = () => setSelectedTickets(new Set());

  const handleBulkAction = async (action, value) => {
    if (selectedTickets.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(selectedTickets);
      if (action === 'status') {
        await api.post('/features/bulk/status', { ticketIds: ids, status: value });
      } else if (action === 'assign') {
        await api.post('/features/bulk/assign', { ticketIds: ids, agentId: value });
      } else if (action === 'priority') {
        await api.post('/features/bulk/priority', { ticketIds: ids, priority: value });
      } else if (action === 'close') {
        await api.post('/features/bulk/close', { ticketIds: ids });
      }
      clearSelection();
      fetchTickets();
    } catch (err) {
      console.error('Erreur bulk:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim() || selectedTickets.size === 0) return;
    setBulkLoading(true);
    try {
      await api.post('/features/broadcast', {
        ticketIds: Array.from(selectedTickets),
        message: broadcastMessage,
        isInternal: broadcastInternal
      });
      setShowBroadcastModal(false);
      setBroadcastMessage('');
      clearSelection();
      fetchTickets();
    } catch (err) {
      console.error('Erreur broadcast:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const fetchSmartViews = async () => {
    try {
      const res = await api.get('/features/smart-views');
      setSmartViews(res.data);
    } catch (err) {
      console.error('Erreur smart views:', err);
    }
  };

  const fetchAvailableAgents = async () => {
    if (!isAgent) return;
    try {
      const res = await api.get('/features/tickets/agents/available');
      setAvailableAgents(res.data);
    } catch (err) {
      console.error('Erreur agents:', err);
    }
  };

  const applySmartView = (view) => {
    const filters = typeof view.filters === 'string' ? JSON.parse(view.filters) : view.filters;
    setActiveSmartView(view.id);
    if (filters.status) {
      setStatusFilter(Array.isArray(filters.status) ? filters.status[0] : filters.status);
    }
    if (filters.priority) setPriorityFilter(filters.priority);
    if (filters.slaBreached) setSlaFilter(true);
  };

  const clearSmartView = () => {
    setActiveSmartView(null);
    setStatusFilter('');
    setPriorityFilter('');
    setSlaFilter(false);
    setCategoryFilter('');
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'list' ? 'kanban' : 'list';
    setViewMode(newMode);
    localStorage.setItem('ticketViewMode', newMode);
  };

  const toggleCompactMode = () => {
    const newMode = !compactMode;
    setCompactMode(newMode);
    localStorage.setItem('ticketCompactMode', String(newMode));
  };

  const toggleColumn = (column) => {
    const newColumns = visibleColumns.includes(column)
      ? visibleColumns.filter(c => c !== column)
      : [...visibleColumns, column];
    setVisibleColumns(newColumns);
    localStorage.setItem('ticketVisibleColumns', JSON.stringify(newColumns));
  };

  // Kanban drag & drop
  const handleDragStart = (e, ticket) => {
    setDraggedTicket(ticket);
    e.dataTransfer.effectAllowed = 'move';
    // Nécessaire pour Firefox
    e.dataTransfer.setData('text/plain', ticket.ticket_number);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTarget !== status) setDropTarget(status);
  };

  const handleDragLeave = (e) => {
    // Vérifier qu'on quitte bien la colonne et pas juste un enfant
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = e;
    if (
      clientX < rect.left || clientX > rect.right ||
      clientY < rect.top || clientY > rect.bottom
    ) {
      setDropTarget(null);
    }
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDropTarget(null);
    if (!draggedTicket || draggedTicket.status === newStatus) {
      setDraggedTicket(null);
      return;
    }

    // Optimistic update
    setTickets(prev => prev.map(t => 
      t.id === draggedTicket.id ? { ...t, status: newStatus } : t
    ));

    try {
      await api.patch(`/tickets/${draggedTicket.ticket_number}`, { status: newStatus });
    } catch (err) {
      console.error('Erreur update status:', err);
      fetchTickets();
    }
    
    setDraggedTicket(null);
  };

  const handleDragEnd = () => {
    setDraggedTicket(null);
    setDropTarget(null);
  };

  // ==========================================
  // HELPERS
  // ==========================================

  const statusLabels = {
    open: 'Ouvert',
    in_progress: 'En cours',
    waiting_user: 'Attente utilisateur',
    waiting_third_party: 'Attente tiers',
    resolved: 'Résolu',
    closed: 'Fermé'
  };

  const statusColors = {
    open: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    waiting_user: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    waiting_third_party: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    resolved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    closed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };

  const statusDotColors = {
    open: 'bg-red-500',
    in_progress: 'bg-blue-500',
    waiting_user: 'bg-yellow-500',
    waiting_third_party: 'bg-orange-500',
    resolved: 'bg-green-500',
    closed: 'bg-gray-400'
  };

  const statusColumnColors = {
    open: 'from-red-500 to-red-600',
    in_progress: 'from-blue-500 to-blue-600',
    waiting_user: 'from-yellow-500 to-yellow-600',
    waiting_third_party: 'from-orange-500 to-orange-600',
    resolved: 'from-green-500 to-green-600',
    closed: 'from-gray-400 to-gray-500'
  };

  const statusBgLight = {
    open: 'bg-gray-50 dark:bg-gray-800/80',
    in_progress: 'bg-gray-50 dark:bg-gray-800/80',
    waiting_user: 'bg-gray-50 dark:bg-gray-800/80',
    waiting_third_party: 'bg-gray-50 dark:bg-gray-800/80',
    resolved: 'bg-gray-50 dark:bg-gray-800/80',
    closed: 'bg-gray-50 dark:bg-gray-800/80'
  };

  const priorityColors = {
    urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };

  const priorityLabels = {
    urgent: 'Urgent',
    high: 'Haute',
    medium: 'Moyenne',
    low: 'Basse'
  };

  // Compteurs par statut (calculés depuis les tickets chargés)
  const statusCounts = useMemo(() => {
    const counts = {};
    tickets.forEach(t => {
      counts[t.status] = (counts[t.status] || 0) + 1;
    });
    return counts;
  }, [tickets]);

  const PriorityIcon = ({ priority, className = "w-3.5 h-3.5" }) => {
    switch (priority) {
      case 'urgent': return <Flame className={`${className} text-red-500`} />;
      case 'high': return <ArrowUp className={`${className} text-orange-500`} />;
      case 'medium': return <Minus className={`${className} text-blue-500`} />;
      case 'low': return <ArrowDown className={`${className} text-gray-400`} />;
      default: return <Circle className={`${className} text-gray-400`} />;
    }
  };

  const isSlaBreached = (ticket) => {
    if (!ticket.sla_due_at || ['resolved', 'closed'].includes(ticket.status)) return false;
    return new Date(ticket.sla_due_at) < new Date();
  };

  const isSlaWarning = (ticket) => {
    if (!ticket.sla_due_at || ['resolved', 'closed'].includes(ticket.status)) return false;
    const remaining = new Date(ticket.sla_due_at) - new Date();
    return remaining > 0 && remaining < 2 * 60 * 60 * 1000; // < 2h
  };

  const getRelativeTime = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}j`;
    return formatDate(dateStr);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const allColumns = [
    { id: 'status', label: 'Statut' },
    { id: 'priority', label: 'Priorité' },
    { id: 'category', label: 'Catégorie' },
    { id: 'user', label: 'Demandeur' },
    { id: 'date', label: 'Date' },
    { id: 'agent', label: 'Agent assigné' },
    { id: 'sla', label: 'SLA' },
  ];

  const kanbanStatuses = ['open', 'in_progress', 'waiting_user', 'waiting_third_party', 'resolved'];

  // ==========================================
  // KANBAN CARD
  // ==========================================
  const renderKanbanCard = (ticket) => {
    const breached = isSlaBreached(ticket);
    const warning = isSlaWarning(ticket);

    return (
      <div
        key={ticket.id}
        draggable={isAgent}
        onDragStart={(e) => handleDragStart(e, ticket)}
        onDragEnd={handleDragEnd}
        className={`group relative bg-white dark:bg-gray-800 rounded-lg border transition-all duration-200 ${
          draggedTicket?.id === ticket.id 
            ? 'opacity-40 scale-95 border-blue-400 dark:border-blue-500 shadow-lg shadow-blue-500/20' 
            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md'
        } ${isAgent ? 'cursor-grab active:cursor-grabbing' : ''} ${
          breached ? 'ring-1 ring-red-300 dark:ring-red-700' : ''
        }`}
      >
        {/* Barre de priorité latérale */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
          ticket.priority === 'urgent' ? 'bg-red-500' :
          ticket.priority === 'high' ? 'bg-orange-500' :
          ticket.priority === 'medium' ? 'bg-blue-500' :
          'bg-gray-300 dark:bg-gray-600'
        }`} />

        <Link to={`/tickets/${ticket.ticket_number}`} className="block p-3 pl-4">
          {/* En-tête: numéro + priorité + SLA */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
              {ticket.ticket_number}
            </span>
            <div className="flex items-center gap-1.5">
              {breached && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 animate-pulse">
                  <AlertCircle className="w-3 h-3" />SLA
                </span>
              )}
              {warning && !breached && (
                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                  <Timer className="w-3 h-3" />Bientôt
                </span>
              )}
              <PriorityIcon priority={ticket.priority} className="w-3.5 h-3.5" />
            </div>
          </div>

          {/* Sujet */}
          <h4 className="text-sm font-medium text-gray-900 dark:text-white leading-snug mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {ticket.subject}
          </h4>

          {/* Catégorie */}
          {ticket.category_name && (
            <div className="mb-2">
              <span 
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                style={{ 
                  backgroundColor: (ticket.category_color || '#6B7280') + '15', 
                  color: ticket.category_color || '#6B7280' 
                }}
              >
                <Tag className="w-2.5 h-2.5" />
                {ticket.category_name}
              </span>
            </div>
          )}

          {/* Footer: demandeur, agent, date */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-600/50">
            <div className="flex items-center gap-2 min-w-0">
              {/* Avatar demandeur */}
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}
                title={ticket.user_name}
              >
                {getInitials(ticket.user_name)}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {ticket.user_name}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Agent assigné */}
              {ticket.agent_name ? (
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-800 flex-shrink-0"
                  style={{ backgroundColor: '#10B981' }}
                  title={`Assigné à ${ticket.agent_name}`}
                >
                  {getInitials(ticket.agent_name)}
                </div>
              ) : (
                <div 
                  className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-600 flex-shrink-0"
                  title="Non assigné"
                >
                  <User className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                </div>
              )}
              {/* Temps relatif */}
              <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums" title={formatDate(ticket.updated_at)}>
                {getRelativeTime(ticket.updated_at)}
              </span>
            </div>
          </div>

          {/* Org badge pour superadmin */}
          {isSuperAdmin && ticket.organization_name && (
            <div className="mt-2 pt-1.5 border-t border-dashed border-gray-100 dark:border-gray-600/50">
              <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                {ticket.organization_name}
              </span>
            </div>
          )}
        </Link>
      </div>
    );
  };

  // ==========================================
  // LIST CARD
  // ==========================================
  const renderTicketCard = (ticket) => {
    const breached = isSlaBreached(ticket);
    const warning = isSlaWarning(ticket);
    const isSelected = selectedTickets.has(ticket.id);

    return (
      <div
        key={ticket.id}
        className={`group flex items-center ${compactMode ? 'px-4 py-2' : 'px-4 py-3'} hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
          breached ? 'bg-red-50/50 dark:bg-red-950/10' : ''
        } ${isSelected ? 'bg-blue-50/50 dark:bg-blue-950/10' : ''}`}
      >
        {/* Checkbox pour bulk actions (agents uniquement) */}
        {isAgent && (
          <button
            onClick={(e) => { e.stopPropagation(); toggleTicketSelection(ticket.id); }}
            className="mr-3 flex-shrink-0 p-0.5"
          >
            {isSelected ? (
              <CheckSquare className="w-4 h-4 text-blue-600" />
            ) : (
              <Square className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-400" />
            )}
          </button>
        )}
      <Link
        to={`/tickets/${ticket.ticket_number}`}
        className="flex-1 min-w-0"
      >
        <div className={`flex items-center gap-${compactMode ? '3' : '4'}`}>
          {/* Indicateur priorité */}
          <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${
            ticket.priority === 'urgent' ? 'bg-red-500' :
            ticket.priority === 'high' ? 'bg-orange-500' :
            ticket.priority === 'medium' ? 'bg-blue-500' :
            'bg-gray-200 dark:bg-gray-600'
          }`} />

          {/* Avatar demandeur */}
          {!compactMode && visibleColumns.includes('user') && (
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ backgroundColor: tenant?.primaryColor || '#3B82F6' }}
              title={ticket.user_name}
            >
              {getInitials(ticket.user_name)}
            </div>
          )}

          {/* Contenu principal */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`${compactMode ? 'text-xs' : 'text-xs'} font-mono text-gray-400 dark:text-gray-500`}>
                {ticket.ticket_number}
              </span>
              {visibleColumns.includes('status') && (
                <span className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${statusDotColors[ticket.status]}`} />
                  <span className={`text-xs font-medium ${
                    ticket.status === 'open' ? 'text-red-600 dark:text-red-400' :
                    ticket.status === 'in_progress' ? 'text-blue-600 dark:text-blue-400' :
                    ticket.status === 'waiting_user' ? 'text-yellow-600 dark:text-yellow-400' :
                    ticket.status === 'waiting_third_party' ? 'text-orange-600 dark:text-orange-400' :
                    ticket.status === 'resolved' ? 'text-green-600 dark:text-green-400' :
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {compactMode ? statusLabels[ticket.status]?.substring(0, 8) : statusLabels[ticket.status]}
                  </span>
                </span>
              )}
              {visibleColumns.includes('priority') && (
                <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                  ticket.priority === 'urgent' ? 'text-red-600 dark:text-red-400' :
                  ticket.priority === 'high' ? 'text-orange-600 dark:text-orange-400' :
                  ticket.priority === 'medium' ? 'text-blue-600 dark:text-blue-400' :
                  'text-gray-500 dark:text-gray-400'
                }`}>
                  <PriorityIcon priority={ticket.priority} className="w-3 h-3" />
                  {!compactMode && priorityLabels[ticket.priority]}
                </span>
              )}
              {visibleColumns.includes('category') && ticket.category_name && (
                <span 
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
                  style={{ backgroundColor: (ticket.category_color || '#6B7280') + '20', color: ticket.category_color || '#6B7280' }}
                >
                  <Tag className="w-2.5 h-2.5" />
                  {compactMode ? ticket.category_name.substring(0, 10) : ticket.category_name}
                </span>
              )}
              {visibleColumns.includes('sla') && breached && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 animate-pulse">
                  <AlertCircle className="w-3 h-3" />SLA dépassé
                </span>
              )}
              {visibleColumns.includes('sla') && warning && !breached && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                  <Timer className="w-3 h-3" />SLA bientôt
                </span>
              )}
              {isSuperAdmin && (
                <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">
                  {ticket.organization_name}
                </span>
              )}
            </div>
            <p className={`font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ${
              compactMode ? 'text-sm truncate' : 'mt-0.5 text-sm'
            }`}>
              {ticket.subject}
            </p>
            {!compactMode && (
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                {visibleColumns.includes('user') && (
                  <span>{ticket.user_name}</span>
                )}
                {visibleColumns.includes('date') && (
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    {getRelativeTime(ticket.updated_at)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Côté droit: agent + chevron */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {!compactMode && visibleColumns.includes('agent') && (
              <div className="text-right hidden sm:block">
                {ticket.agent_name ? (
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Assigné à</p>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{ticket.agent_name}</p>
                    </div>
                    <div 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ backgroundColor: '#10B981' }}
                    >
                      {getInitials(ticket.agent_name)}
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500 italic">Non assigné</span>
                )}
              </div>
            )}
            <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors" />
          </div>
        </div>
      </Link>
      </div>
    );
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Ticket className="w-7 h-7" />Tickets
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {tickets.length} ticket{tickets.length > 1 ? 's' : ''}
            {refreshing && <RefreshCw className="inline w-3 h-3 ml-2 animate-spin" />}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Mode compact */}
          <button
            onClick={toggleCompactMode}
            className={`p-2 rounded-lg transition-colors ${compactMode ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            title="Mode compact (C)"
          >
            {compactMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Personnaliser colonnes */}
          <div className="relative">
            <button
              onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="Personnaliser colonnes"
            >
              <Settings2 className="w-4 h-4" />
            </button>
            {showColumnsMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowColumnsMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-2">
                  <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Colonnes visibles</p>
                  {allColumns.map(col => (
                    <label key={col.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.id)}
                        onChange={() => toggleColumn(col.id)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{col.label}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Rafraîchir */}
          <button
            onClick={() => fetchTickets()}
            disabled={refreshing}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Rafraîchir (R)"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Vue liste / Kanban */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => { setViewMode('list'); localStorage.setItem('ticketViewMode', 'list'); }}
              className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
              title="Vue liste (V)"
            >
              <LayoutList className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setViewMode('kanban'); localStorage.setItem('ticketViewMode', 'kanban'); }}
              className={`p-2 rounded transition-colors ${viewMode === 'kanban' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
              title="Vue Kanban (V)"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>

          {/* Nouveau ticket */}
          <button onClick={() => setShowNewTicketModal(true)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Nouveau ticket</span>
            <span className="sm:hidden">Nouveau</span>
          </button>
        </div>
      </div>

      {/* Smart Views */}
      {smartViews.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeSmartView && (
            <button onClick={clearSmartView} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600">
              <X className="w-3 h-3" /> Effacer vue
            </button>
          )}
          {smartViews.map(view => (
            <button
              key={view.id}
              onClick={() => applySmartView(view)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeSmartView === view.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500'
              }`}
            >
              <Eye className="w-3 h-3" />
              {view.name}
            </button>
          ))}
        </div>
      )}

      {/* Bulk Action Bar */}
      {showBulkBar && isAgent && (
        <div className="card p-3 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 animate-fade-in">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {selectedTickets.size} sélectionné{selectedTickets.size > 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                onChange={(e) => { if (e.target.value) handleBulkAction('status', e.target.value); e.target.value = ''; }}
                className="input text-sm py-1 w-auto"
                disabled={bulkLoading}
              >
                <option value="">Changer statut...</option>
                <option value="open">Ouvert</option>
                <option value="in_progress">En cours</option>
                <option value="waiting_user">Attente utilisateur</option>
                <option value="resolved">Résolu</option>
              </select>
              <select
                onChange={(e) => { if (e.target.value) handleBulkAction('priority', e.target.value); e.target.value = ''; }}
                className="input text-sm py-1 w-auto"
                disabled={bulkLoading}
              >
                <option value="">Changer priorité...</option>
                <option value="urgent">Urgent</option>
                <option value="high">Haute</option>
                <option value="medium">Moyenne</option>
                <option value="low">Basse</option>
              </select>
              {availableAgents.length > 0 && (
                <select
                  onChange={(e) => { if (e.target.value) handleBulkAction('assign', parseInt(e.target.value)); e.target.value = ''; }}
                  className="input text-sm py-1 w-auto"
                  disabled={bulkLoading}
                >
                  <option value="">Assigner à...</option>
                  {availableAgents.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              )}
              <button
                onClick={() => handleBulkAction('close')}
                disabled={bulkLoading}
                className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50"
              >
                Fermer
              </button>
              <button
                onClick={() => setShowBroadcastModal(true)}
                disabled={bulkLoading}
                className="px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 flex items-center gap-1"
              >
                <Megaphone className="w-3 h-3" /> Message
              </button>
            </div>
            <button onClick={clearSelection} className="ml-auto text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
            {bulkLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
          </div>
        </div>
      )}

      {/* Status counter pills */}
      {tickets.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusLabels).map(([key, label]) => {
            const count = statusCounts[key] || 0;
            if (count === 0) return null;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:shadow-sm ${statusColors[key]} ${statusFilter === key ? 'ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500' : ''}`}
              >
                <span className={`w-2 h-2 rounded-full ${statusDotColors[key]}`} />
                {count} {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher... (appuyez sur /)"
              className="input pl-10"
            />
          </div>
          {viewMode === 'list' && (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input w-auto">
              <option value="">Tous les statuts</option>
              {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="input w-auto">
            <option value="">Toutes priorités</option>
            <option value="urgent">Urgent</option>
            <option value="high">Haute</option>
            <option value="medium">Moyenne</option>
            <option value="low">Basse</option>
          </select>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="input w-auto">
            <option value="">Toutes catégories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {isAgent && (
            <select
              value={slaFilter ? 'sla' : ''}
              onChange={(e) => setSlaFilter(e.target.value === 'sla')}
              className="input w-auto"
            >
              <option value="">SLA : Tous</option>
              <option value="sla">⏱ SLA dépassé</option>
            </select>
          )}
          {isSuperAdmin && accessibleOrganizations.length > 1 && (
            <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)} className="input w-auto">
              <option value="">Toutes orgs</option>
              {accessibleOrganizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <button
            onClick={() => setShowShortcutsHelp(true)}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1 transition-colors"
          >
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">?</kbd>
            Raccourcis
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
            <Ticket className="w-8 h-8 text-gray-300 dark:text-gray-600" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">Aucun ticket trouvé</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Modifiez vos filtres ou créez un nouveau ticket</p>
        </div>
      ) : viewMode === 'kanban' ? (
        /* ==========================================
           KANBAN VIEW
           ========================================== */
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-2 px-2">
          {kanbanStatuses.map(status => {
            const statusTickets = tickets.filter(t => t.status === status);
            const isDropTarget = dropTarget === status && draggedTicket && draggedTicket.status !== status;
            
            return (
              <div 
                key={status}
                className={`flex-shrink-0 w-80 rounded-xl transition-all duration-200 ${
                  isDropTarget 
                    ? 'ring-2 ring-blue-400 dark:ring-blue-500 ring-offset-2 dark:ring-offset-gray-900 scale-[1.01]' 
                    : ''
                }`}
                onDragOver={(e) => handleDragOver(e, status)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status)}
              >
                {/* Column Header */}
                <div className={`bg-gradient-to-r ${statusColumnColors[status]} rounded-t-xl px-4 py-2.5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold text-sm">
                        {statusLabels[status]}
                      </span>
                    </div>
                    <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center">
                      {statusTickets.length}
                    </span>
                  </div>
                </div>

                {/* Column Body */}
                <div className={`${statusBgLight[status]} border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl`}>
                  <div className={`p-2 space-y-2 min-h-[80px] max-h-[calc(100vh-320px)] overflow-y-auto`}>
                    {statusTickets.map(ticket => renderKanbanCard(ticket))}
                    
                    {/* Zone de drop vide */}
                    {statusTickets.length === 0 && (
                      <div className={`flex flex-col items-center justify-center py-6 rounded-lg border border-dashed transition-colors ${
                        isDropTarget 
                          ? 'border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/20' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        <p className={`text-xs ${isDropTarget ? 'text-blue-500 dark:text-blue-400 font-medium' : 'text-gray-400 dark:text-gray-500'}`}>
                          {isDropTarget ? 'Déposer ici' : 'Aucun ticket'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ==========================================
           LIST VIEW
           ========================================== */
        <div className="card overflow-hidden">
          {/* Select All header (agents only) */}
          {isAgent && tickets.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700/50 flex items-center gap-3">
              <button onClick={selectAllTickets} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                {selectedTickets.size === tickets.length && tickets.length > 0 ? (
                  <CheckSquare className="w-4 h-4 text-blue-600" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                Tout sélectionner ({tickets.length})
              </button>
            </div>
          )}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {tickets.map(ticket => renderTicketCard(ticket))}
          </div>
        </div>
      )}

      {/* Modals */}
      <NewTicketModal
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
      />

      <ShortcutsHelp
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
      />

      {/* Broadcast Modal */}
      {showBroadcastModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                Message groupé
              </h2>
              <button onClick={() => setShowBroadcastModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Envoyer un message à <span className="font-bold">{selectedTickets.size}</span> ticket{selectedTickets.size > 1 ? 's' : ''} sélectionné{selectedTickets.size > 1 ? 's' : ''}
              </p>
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                className="input min-h-[120px]"
                placeholder="Votre message..."
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={broadcastInternal}
                  onChange={(e) => setBroadcastInternal(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Note interne (invisible pour l'utilisateur)</span>
              </label>
              <div className="flex gap-3">
                <button onClick={() => setShowBroadcastModal(false)} className="btn-secondary flex-1">Annuler</button>
                <button
                  onClick={handleBroadcast}
                  disabled={bulkLoading || !broadcastMessage.trim()}
                  className="btn-primary flex-1"
                >
                  {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Megaphone className="w-4 h-4 mr-2" />}
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketList;